import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import StickyBottomNav from "../../components/Navbar/StickyBottomNav";
import RandomImageGallery from "../../components/RandomGallery.js/RandomGallery";
import { Heart, BookOpen, UserPlus, MessageSquare, ArrowRight, Shield } from 'lucide-react';

const DonateButton = ({ children, onClick, variant = "primary", className = "" }) => {
  return (
    <motion.button
      onClick={onClick}
      className={`py-3 px-8 rounded-xl text-button font-body shadow-md flex items-center justify-center ${
        variant === "primary" 
          ? "bg-cta text-white hover:bg-cta/90" 
          : "bg-white border border-primary text-primary hover:bg-primary/5"
      } ${className}`}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  );
};

const StatCard = ({ number, label, description }) => (
  <motion.div 
    className="bg-white rounded-xl shadow-md p-6 text-center"
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
  >
    <h3 className="font-display text-h2 text-cta mb-1">{number}</h3>
    <p className="font-display text-h3 text-secondary mb-3">{label}</p>
    <p className="text-textPrimary">{description}</p>
    <div className="w-16 h-1 bg-cta mx-auto mt-4"></div>
  </motion.div>
);

const ImpactCard = ({ icon, title, description }) => (
  <motion.div 
    className="text-center p-6 bg-white rounded-xl shadow-md"
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
  >
    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
      {icon}
    </div>
    <h3 className="font-display text-h3 text-secondary mb-3">{title}</h3>
    <p className="text-textPrimary">{description}</p>
    <div className="w-12 h-1 bg-cta mx-auto mt-4"></div>
  </motion.div>
);

function Donate() {
  const navigateToExternalPage = () => {
    window.open(
      "https://buy.stripe.com/6oEfZ622UcD48ak4gg",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const impactAreas = [
    {
      icon: <Heart size={32} className="text-primary" />,
      title: "Emergency Relief",
      description:
        "Providing essential supplies including food, hygiene products, and clothing to refugees in immediate need."
    },
    {
      icon: <BookOpen size={32} className="text-primary" />,
      title: "Education Support",
      description:
        "Offering tutoring, language assistance, and educational resources to help refugee students thrive in their new environment."
    },
    {
      icon: <MessageSquare size={32} className="text-primary" />,
      title: "Speaker Series",
      description:
        "Hosting refugee speakers and organizing events to share personal stories, raise awareness, and foster understanding of refugee experiences."
    }
  ];

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="pt-6"
            >
              <h1 className="font-display text-h1 text-secondary mb-6 leading-tight">
                EMPOWER CHANGE,
                <br />
                ONE DONATION
                <br />
                AT A TIME
              </h1>
              <div className="w-16 h-1 bg-cta mb-6"></div>
              
              <p className="text-bodyLarge text-textPrimary mb-8 max-w-xl">
                Join us in creating lasting impact for refugee families. Your
                generosity directly supports essential services, education, and
                community integration programs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <DonateButton 
                  onClick={navigateToExternalPage} 
                  className="w-full sm:w-auto"
                >
                  <Heart size={20} className="mr-2" />
                  Donate Now
                </DonateButton>
                
                <Link to="/impact" className="w-full sm:w-auto">
                  <DonateButton 
                    variant="secondary"
                    className="w-full"
                  >
                    See Our Impact
                    <ArrowRight size={16} className="ml-2" />
                  </DonateButton>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col pt-6"
            >
              <div className="mb-6">
                <RandomImageGallery />
              </div>
              
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-md flex items-start"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Shield size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-h3 text-secondary mb-2">100% Donation Impact</h3>
                  <p className="text-textPrimary">
                    Every dollar you donate goes directly to supporting refugee
                    families. We maintain complete transparency in our operations,
                    ensuring your contribution creates maximum impact.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Impact Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-h2 text-secondary mb-2">Your Support Creates Real Change</h2>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              See the impact we're making together for refugee communities
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <StatCard 
              number="1,000+" 
              label="Hours of Tutoring"
              description="Helping refugee students excel in their education"
            />
            <StatCard 
              number="4,000+" 
              label="Refugees Supported"
              description="Direct aid and resources provided to families"
            />
            <StatCard 
              number="100+" 
              label="Active Members"
              description="Dedicated volunteers creating change"
            />
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-h2 text-secondary mb-2">How Your Donation Makes an Impact</h2>
            <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
              Your generosity creates meaningful change in these key areas
            </p>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {impactAreas.map((area, index) => (
              <ImpactCard
                key={index}
                icon={area.icon}
                title={area.title}
                description={area.description}
              />
            ))}
          </div>

          {/* Testimonial */}
          <motion.div 
            className="bg-white rounded-xl p-8 shadow-md max-w-3xl mx-auto text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <UserPlus size={32} className="text-primary" />
            </div>
            <blockquote className="text-bodyLarge text-secondary italic mb-6">
              "The support from Students for Refugees has been life-changing for
              our family. The educational assistance and community support have
              helped us build a new life here."
            </blockquote>
            <div className="w-16 h-1 bg-cta mx-auto mb-4"></div>
            <p className="text-textPrimary font-bold">— Recent Program Participant</p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-h2 text-secondary mb-4">
              Ready to Make a Difference?
            </h2>
            <p className="text-bodyLarge text-textPrimary mb-8 max-w-2xl mx-auto">
              Your donation today helps create a better tomorrow for refugee
              families. Join our community of supporters.
            </p>
            
            <DonateButton 
              onClick={navigateToExternalPage} 
              className="mx-auto"
            >
              <Heart size={20} className="mr-2" />
              Donate Now
            </DonateButton>
          </motion.div>
        </div>
      </section>

      <Footer />
      <StickyBottomNav />
    </div>
  );
}

export default Donate;